import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Sun, HelpingHand, Check, Loader2 } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { getCurrentLocation, getBatteryLevel, vibrate, formatDateLong } from "../lib/util";
import InstallPrompt from "../components/InstallPrompt";

function CheckInCard({ onDone }) {
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);

  const choose = async (r) => {
    if (busy) return;
    // IMMEDIATE tactile + visual feedback so the user knows the tap landed.
    vibrate(30);
    setBusy(true);
    setChosen(r);
    try {
      const res = await api.post("/events/check-in", { response: r });
      // If the user tapped "Need help" from the daily check-in, ALSO fire the
      // silent SOS broadcast so their Trust Circle actually gets notified.
      // Without this, the button records the response but no alert is sent -
      // which is dangerously misleading given the bright-red urgent styling.
      let helpRes = null;
      if (r === "help") {
        try {
          const [loc, battery] = await Promise.all([
            getCurrentLocation(7000, true).catch(() => null),
            getBatteryLevel().catch(() => null),
          ]);
          const hr = await api.post("/events/help", { lat: loc?.lat, lng: loc?.lng, battery, source: "checkin_help" });
          helpRes = hr.data;
        } catch (e) {
          // Broadcast may fail (network, backend) - the check-in still recorded.
          // Non-fatal here; the SOS long-press button remains as a safety net.
        }
      }
      // Longer confirm delay so the user actually sees the green tick land.
      setTimeout(() => { onDone && onDone(r, res.data, helpRes); }, 1100);
    } catch {
      setBusy(false);
      setChosen(null);
    }
  };

  const options = [
    { id: "okay", label: "Okay", cls: "bg-sage-500 text-white", ring: "ring-sage-600/60", testId: "checkin-okay" },
    { id: "callme", label: "Call me", cls: "bg-white text-ink-900 border border-sand-200", ring: "ring-terracotta-500/60", testId: "checkin-callme" },
    { id: "help", label: "Need help", cls: "bg-brick-500 text-white", ring: "ring-brick-700/60", testId: "checkin-help" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-3xl bg-terracotta-50 border border-terracotta-400/30 p-5"
      data-testid="check-in-card"
    >
      <div className="flex items-center gap-2">
        <Sun size={16} className="text-terracotta-600"/>
        <p className="text-[11px] tracking-[0.18em] uppercase text-terracotta-600 font-semibold">Daily check-in</p>
      </div>
      <p className="font-display text-[22px] text-ink-900 leading-tight mt-2">How are you feeling today?</p>
      <div className="mt-4 grid grid-cols-3 gap-2">
        {options.map((b) => {
          const isChosen = chosen === b.id;
          const isDim = busy && !isChosen;
          return (
            <button
              key={b.id}
              onClick={() => choose(b.id)}
              disabled={busy}
              data-testid={b.testId}
              className={`relative h-[56px] rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-[0.94] overflow-hidden
                ${b.cls}
                ${isChosen ? `ring-4 ${b.ring} scale-[1.02] shadow-lifted` : ""}
                ${isDim ? "opacity-40" : "opacity-100"}`}
            >
              <AnimatePresence mode="wait">
                {isChosen ? (
                  <motion.span
                    key="chosen"
                    initial={{ opacity: 0, scale: 0.6 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="absolute inset-0 flex items-center justify-center gap-1.5"
                  >
                    <Check size={18} strokeWidth={3} />
                    <span>Sent</span>
                  </motion.span>
                ) : (
                  <motion.span
                    key="label"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    {b.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </button>
          );
        })}
      </div>
      {busy && !chosen && (
        <div className="mt-3 flex items-center gap-2 text-[12px] text-terracotta-700">
          <Loader2 size={14} className="animate-spin" />
          <span>Recording…</span>
        </div>
      )}
    </motion.div>
  );
}

function SafeButton({ onBroadcast }) {
  const [busy, setBusy] = useState(false);
  const [ripples, setRipples] = useState([]);

  const handleTap = async () => {
    if (busy) return;
    setBusy(true);
    vibrate(40);
    setRipples((r) => [...r, Date.now()]);
    try {
      const loc = await getCurrentLocation(1500);
      const res = await api.post("/events/safe", { lat: loc?.lat, lng: loc?.lng });
      onBroadcast && onBroadcast(res.data);
    } catch {}
    setTimeout(() => setBusy(false), 900);
  };

  return (
    <div className="relative flex flex-col items-center" data-testid="im-safe-wrap">
      <button
        onClick={handleTap}
        data-testid="im-safe-button"
        className="relative h-[180px] w-[180px] rounded-full bg-sage-500 hover:bg-sage-600 active:scale-[0.97] transition shadow-lifted text-white flex flex-col items-center justify-center"
      >
        {ripples.map((id) => (
          <span
            key={id}
            onAnimationEnd={() => setRipples((r) => r.filter((x) => x !== id))}
            className="absolute inset-0 rounded-full border-4 border-sage-400 animate-ripple"
          />
        ))}
        <ShieldCheck size={42} strokeWidth={2} />
        <span className="font-display text-[26px] mt-2 leading-none">I am Safe</span>
        <span className="text-[11px] mt-1 tracking-[0.14em] uppercase opacity-80">Tap once</span>
      </button>
    </div>
  );
}

function SOSButton({ onTrigger }) {
  const HOLD_MS = 2200;
  const [progress, setProgress] = useState(0);
  const [armed, setArmed] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);

  useEffect(() => {
    if (!armed) return;
    const start = Date.now();
    const iv = setInterval(() => {
      const p = Math.min(1, (Date.now() - start) / HOLD_MS);
      setProgress(p);
      if (p >= 1) {
        clearInterval(iv);
        fire();
      }
    }, 40);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed]);

  const cancel = () => {
    setArmed(false);
    setProgress(0);
  };

  const fire = async () => {
    setArmed(false);
    setBroadcasting(true);
    vibrate([60, 40, 60, 40, 120]);
    try {
      // High-accuracy GPS with 7s window. Mobile GPS often needs 3-8s to lock,
      // and network-only positioning is off by kilometres - useless for family
      // trying to reach the user. Battery hit is negligible for a one-shot call.
      const [loc, battery] = await Promise.all([getCurrentLocation(7000, true), getBatteryLevel()]);
      const res = await api.post("/events/help", { lat: loc?.lat, lng: loc?.lng, battery, source: "sos" });
      onTrigger && onTrigger(res.data);
    } catch {}
    setTimeout(() => { setBroadcasting(false); setProgress(0); }, 2400);
  };

  return (
    <div className="relative" data-testid="sos-wrap">
      <button
        onPointerDown={() => setArmed(true)}
        onPointerUp={cancel}
        onPointerLeave={cancel}
        onPointerCancel={cancel}
        data-testid="sos-button"
        className={`relative w-full h-[72px] rounded-full overflow-hidden text-white font-semibold flex items-center justify-center gap-2
          ${broadcasting ? "bg-brick-600 animate-pulseRed" : "bg-brick-500 active:bg-brick-600"}`}
      >
        {/* progress fill */}
        <span
          className="absolute left-0 top-0 bottom-0 bg-brick-600 transition-[width] duration-75"
          style={{ width: `${progress * 100}%` }}
        />
        <span className="relative flex items-center gap-2">
          <HelpingHand size={20} />
          <span className="text-[16px] tracking-wide">
            {broadcasting
              ? "Broadcasting live location…"
              : armed
                ? "Keep holding to send SOS…"
                : "Hold for 2 seconds: Need Help (SOS)"}
          </span>
        </span>
      </button>
      <p className="text-center text-[11px] text-ink-500 mt-2">
        Long-press prevents accidental triggers · Your circle gets your location silently
      </p>
    </div>
  );
}

export default function Home({ onJump }) {
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const [checkIn, setCheckIn] = useState(null); // { response, broadcast }
  const [stats, setStats] = useState({ contacts: 0, lastEvent: null });

  const refreshStats = async () => {
    try {
      const [c, e] = await Promise.all([api.get("/circle"), api.get("/events?limit=1")]);
      setStats({ contacts: c.data.length, lastEvent: e.data[0] || null });
    } catch {}
  };

  useEffect(() => { refreshStats(); }, []);

  const showToast = (msg, tone) => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3200);
  };

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  })();
  const firstName = (user?.name || "friend").split(" ")[0];

  return (
    <div className="px-6 pt-4" data-testid="home-screen">
      {/* header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[12px] tracking-[0.16em] uppercase text-ink-500">{formatDateLong()}</p>
          <h1 className="font-display text-[34px] leading-[1.05] text-ink-900 mt-1 tracking-tight">
            {greeting},<br/>
            <span className="text-sage-700">{firstName}.</span>
          </h1>
        </div>
        <button
          onClick={() => onJump && onJump("circle")}
          data-testid="home-circle-shortcut"
          className="h-11 px-4 rounded-full bg-white border border-sand-200 text-sm font-semibold text-ink-900 flex items-center gap-2"
        >
          {stats.contacts} in circle
        </button>
      </div>

      {/* PWA install nudge - only renders if the browser reports installable */}
      <InstallPrompt/>

      {/* check-in card */}
      <div className="mt-5">
        {!checkIn ? (
          <CheckInCard onDone={(r, checkInData, helpData) => {
            const helpN = helpData
              ? (helpData?.broadcast?.sent_wa || 0) + (helpData?.broadcast?.sent_sms || 0)
              : 0;
            const okayN = r === "okay"
              ? (checkInData?.broadcast?.sent_wa || 0) + (checkInData?.broadcast?.sent_sms || 0)
              : 0;
            const callN = r === "callme"
              ? (checkInData?.broadcast?.sent_sms || 0) + (checkInData?.broadcast?.sent_wa || 0)
              : 0;
            setCheckIn({ response: r, helpN, callN, okayN });
            const msg = r === "okay"
              ? (okayN > 0
                  ? `Thanks! Reassurance sent to ${okayN} contact${okayN === 1 ? "" : "s"}.`
                  : "Thanks! Check-in recorded. Add contacts to your circle so they hear it next time.")
              : r === "callme"
                ? (callN > 0
                    ? `Call-me nudge sent to ${callN} contact${callN === 1 ? "" : "s"}.`
                    : "Call-me recorded. Add contacts to your circle so they hear it next time.")
                : helpN > 0
                  ? `Help signal sent to ${helpN} contact${helpN === 1 ? "" : "s"}.`
                  : "Help signal recorded. Add contacts to your circle so they hear it next time.";
            showToast(msg, r === "help" ? "danger" : "ok");
            refreshStats();
          }}/>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`rounded-3xl p-5 border ${
              checkIn.response === "help"
                ? "bg-brick-50 border-brick-500/30"
                : "bg-sage-50 border-sage-400/30"
            }`}
            data-testid="check-in-done-card"
          >
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                checkIn.response === "help" ? "bg-brick-500 text-white" : "bg-sage-500 text-white"
              }`}>
                <Check size={18} strokeWidth={3} />
              </div>
              <p className={`text-[11px] tracking-[0.18em] uppercase font-semibold ${
                checkIn.response === "help" ? "text-brick-700" : "text-sage-700"
              }`}>
                {checkIn.response === "okay" && "Okay - recorded"}
                {checkIn.response === "callme" && "Call me - recorded"}
                {checkIn.response === "help" && "Help signal sent"}
              </p>
            </div>
            <p className="font-display text-[20px] text-ink-900 leading-tight mt-3">
              {checkIn.response === "okay" && (checkIn.okayN > 0 ? `Reassurance sent to ${checkIn.okayN} contact${checkIn.okayN === 1 ? "" : "s"}.` : "Thanks for the check-in.")}
              {checkIn.response === "callme" && (checkIn.callN > 0 ? `Nudge sent to ${checkIn.callN} contact${checkIn.callN === 1 ? "" : "s"} - asking them to call you.` : "Nudge recorded. Add contacts so they hear it next time.")}
              {checkIn.response === "help" && (checkIn.helpN > 0 ? `Alert sent to ${checkIn.helpN} contact${checkIn.helpN === 1 ? "" : "s"}.` : "Help logged. Add contacts to your circle.")}
            </p>
            <p className="text-sm text-ink-700 mt-2">
              {checkIn.response === "help"
                ? "You can also hold the red SOS button below to broadcast your live location."
                : "We'll ask again tomorrow morning."}
            </p>
            <button
              onClick={() => setCheckIn(null)}
              data-testid="check-in-change"
              className="mt-3 text-[12px] font-semibold text-ink-500 underline underline-offset-2"
            >
              Change my answer
            </button>
          </motion.div>
        )}
      </div>

      {/* big safe button */}
      <div className="mt-8 flex flex-col items-center">
        <SafeButton onBroadcast={(r) => {
          const n = (r?.broadcast?.sent_wa || 0) + (r?.broadcast?.sent_sms || 0);
          showToast(
            n > 0
              ? `Safe ping sent to ${n} contact${n === 1 ? "" : "s"}.`
              : "Safe ping recorded. Add contacts to your circle to alert them.",
            "ok"
          );
          refreshStats();
        }}/>
      </div>

      {/* sos */}
      <div className="mt-8 sticky bottom-[96px]">
        <SOSButton onTrigger={(r) => {
          const n = (r?.broadcast?.sent_wa || 0) + (r?.broadcast?.sent_sms || 0);
          showToast(
            n > 0
              ? `SOS broadcast to ${n} contact${n === 1 ? "" : "s"}.`
              : "SOS logged. Add contacts to your circle so they can respond.",
            "danger"
          );
          refreshStats();
        }}/>
      </div>

      {/* toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className={`fixed left-1/2 -translate-x-1/2 top-[52px] z-50 px-5 py-3 rounded-full shadow-lifted text-sm font-medium max-w-[90%] text-center
              ${toast.tone === "danger" ? "bg-brick-500 text-white" : "bg-ink-900 text-white"}`}
            data-testid="home-toast"
          >
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
